import React, { useEffect, useState, useCallback } from "react";
import DataGrid, { Column, Editing, Button as GridButton } from "devextreme-react/data-grid";
import { TabPanel } from "devextreme-react/tab-panel";
import { useTranslation } from "../../contexts/TranslationContext";
import {
	getCupidSeasons,
	upsertCupidSeason,
	deleteCupidSeason,
	getCupidPrizes,
	upsertCupidPrize,
	deleteCupidPrize,
	getCupidAdminLeaderboard,
	getCupidWinners,
	markCupidWinnerGranted,
} from "../../api/apiServiceFetch";
import "./GameCupid.scss";

const REWARD_KINDS = [
	{ id: "premium_days", labelKey: "cupid_reward_premium_days" },
	{ id: "super_likes", labelKey: "cupid_reward_super_likes" },
	{ id: "clean_slate", labelKey: "cupid_reward_clean_slate" },
	{ id: "manual", labelKey: "cupid_reward_manual" },
];

const WINNER_STATUSES = [
	{ id: "granted", labelKey: "cupid_status_granted" },
	{ id: "manual_required", labelKey: "cupid_status_manual_required" },
	{ id: "failed", labelKey: "cupid_status_failed" },
];

const lookupSource = (t, items) => items.map((i) => ({ id: i.id, label: t(i.labelKey) }));

// Two seasons can both be called "Сентябрь" - one per year - so the admin label
// carries the year even though the season name deliberately does not.
const seasonLabel = (season) => {
	const year = season.startsAt ? new Date(season.startsAt).getFullYear() : "";
	return year ? `${season.nameRu} ${year}` : season.nameRu;
};

// Prizes with seasonId 0 are the template every auto-created season is stamped from.
const seasonOptions = (t, seasons) => [
	{ id: 0, label: t("cupid_season_template") },
	...seasons.map((s) => ({ id: s.id, label: seasonLabel(s) })),
];

const PrizesTab = ({ t, seasons }) => {
	const [prizes, setPrizes] = useState([]);

	const reload = useCallback(async () => {
		try {
			setPrizes(await getCupidPrizes());
		} catch (e) {
			console.error("Load prizes error:", e);
		}
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	const handleRowInserting = async (e) => {
		e.cancel = true;
		try {
			await upsertCupidPrize({ ...e.data, id: 0 });
			await reload();
		} catch (err) {
			console.error("Insert prize error:", err);
		}
	};

	const handleRowUpdating = async (e) => {
		e.cancel = true;
		const updated = { ...e.oldData, ...e.newData };
		try {
			await upsertCupidPrize(updated);
			await reload();
		} catch (err) {
			console.error("Update prize error:", err);
		}
	};

	const handleRowRemoving = async (e) => {
		e.cancel = true;
		try {
			await deleteCupidPrize(e.data.id);
			await reload();
		} catch (err) {
			console.error("Delete prize error:", err);
		}
	};

	return (
		<DataGrid
			dataSource={prizes}
			keyExpr="id"
			showBorders={true}
			onRowInserting={handleRowInserting}
			onRowUpdating={handleRowUpdating}
			onRowRemoving={handleRowRemoving}
		>
			<Editing mode="row" allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true} />
			<Column dataField="id" caption="ID" allowEditing={false} width={60} />
			<Column
				dataField="seasonId"
				caption={t("cupid_col_season")}
				lookup={{ dataSource: seasonOptions(t, seasons), valueExpr: "id", displayExpr: "label" }}
			/>
			<Column dataField="rank" caption={t("cupid_col_rank")} dataType="number" width={90} />
			<Column dataField="titleRu" caption={t("cupid_col_title_ru")} />
			<Column dataField="titleHy" caption={t("cupid_col_title_hy")} />
			<Column dataField="titleEn" caption={t("cupid_col_title_en")} />
			<Column dataField="valueRu" caption={t("cupid_col_value_ru")} />
			<Column dataField="valueHy" caption={t("cupid_col_value_hy")} />
			<Column dataField="valueEn" caption={t("cupid_col_value_en")} />
			<Column dataField="sortOrder" caption={t("cupid_col_sort")} dataType="number" width={90} />
			<Column
				dataField="rewardKind"
				caption={t("cupid_col_reward_kind")}
				lookup={{ dataSource: lookupSource(t, REWARD_KINDS), valueExpr: "id", displayExpr: "label" }}
			/>
			<Column dataField="rewardAmount" caption={t("cupid_col_reward_amount")} dataType="number" width={110} />
			<Column dataField="isActive" caption={t("cupid_col_active")} dataType="boolean" width={100} />
		</DataGrid>
	);
};

const SeasonsTab = ({ t }) => {
	const [seasons, setSeasons] = useState([]);

	const reload = useCallback(async () => {
		try {
			setSeasons(await getCupidSeasons());
		} catch (e) {
			console.error("Load seasons error:", e);
		}
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	const handleRowInserting = async (e) => {
		e.cancel = true;
		try {
			await upsertCupidSeason({ ...e.data, id: 0 });
			await reload();
		} catch (err) {
			console.error("Insert season error:", err);
		}
	};

	const handleRowUpdating = async (e) => {
		e.cancel = true;
		const updated = { ...e.oldData, ...e.newData };
		try {
			await upsertCupidSeason(updated);
			await reload();
		} catch (err) {
			console.error("Update season error:", err);
		}
	};

	const handleRowRemoving = async (e) => {
		e.cancel = true;
		try {
			await deleteCupidSeason(e.data.id);
			await reload();
		} catch (err) {
			console.error("Delete season error:", err);
		}
	};

	return (
		<DataGrid
			dataSource={seasons}
			keyExpr="id"
			showBorders={true}
			onRowInserting={handleRowInserting}
			onRowUpdating={handleRowUpdating}
			onRowRemoving={handleRowRemoving}
		>
			<Editing mode="row" allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true} />
			<Column dataField="id" caption="ID" allowEditing={false} width={60} />
			<Column dataField="nameRu" caption={t("cupid_col_name_ru")} />
			<Column dataField="nameHy" caption={t("cupid_col_name_hy")} />
			<Column dataField="nameEn" caption={t("cupid_col_name_en")} />
			<Column dataField="startsAt" caption={t("cupid_col_starts")} dataType="datetime" />
			<Column dataField="endsAt" caption={t("cupid_col_ends")} dataType="datetime" />
			<Column dataField="isActive" caption={t("cupid_col_active")} dataType="boolean" width={100} />
		</DataGrid>
	);
};

const LeaderboardTab = ({ t, seasons }) => {
	const [type, setType] = useState("monthly");
	const [seasonId, setSeasonId] = useState("");
	const [rows, setRows] = useState([]);

	useEffect(() => {
		(async () => {
			try {
				const result = await getCupidAdminLeaderboard(
					type === "all_time" ? "" : seasonId,
					type,
				);
				setRows(result.players || []);
			} catch (e) {
				console.error("Load leaderboard error:", e);
			}
		})();
	}, [type, seasonId]);

	return (
		<div>
			<div className="game-cupid__toolbar">
				<select value={type} onChange={(e) => setType(e.target.value)}>
					<option value="monthly">{t("cupid_lb_type_monthly")}</option>
					<option value="all_time">{t("cupid_lb_type_all_time")}</option>
				</select>
				{type === "monthly" && (
					<select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
						<option value="">—</option>
						{seasons.map((s) => (
							<option key={s.id} value={s.id}>
								{s.nameRu}
							</option>
						))}
					</select>
				)}
			</div>
			<DataGrid dataSource={rows} keyExpr="userId" showBorders={true}>
				<Column dataField="rank" caption={t("cupid_col_rank")} width={90} />
				<Column dataField="name" caption={t("cupid_col_player")} />
				<Column dataField="userId" caption="UserId" width={120} />
				<Column dataField="score" caption={t("cupid_col_score")} width={120} />
			</DataGrid>
		</div>
	);
};

const WinnersTab = ({ t, seasons }) => {
	const [seasonId, setSeasonId] = useState("");
	const [rows, setRows] = useState([]);

	const reload = useCallback(async () => {
		try {
			setRows(await getCupidWinners(seasonId));
		} catch (e) {
			console.error("Load winners error:", e);
		}
	}, [seasonId]);

	useEffect(() => {
		reload();
	}, [reload]);

	const handleMarkGranted = async (winner) => {
		try {
			await markCupidWinnerGranted(winner.id, null);
			await reload();
		} catch (e) {
			console.error("Mark winner granted error:", e);
		}
	};

	return (
		<div>
			<div className="game-cupid__toolbar">
				<select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
					<option value="">—</option>
					{seasons.map((s) => (
						<option key={s.id} value={s.id}>
							{seasonLabel(s)}
						</option>
					))}
				</select>
			</div>
			<DataGrid dataSource={rows} keyExpr="id" showBorders={true}>
				<Column dataField="rank" caption={t("cupid_col_rank")} width={80} />
				<Column dataField="name" caption={t("cupid_col_player")} />
				<Column dataField="userId" caption="UserId" width={110} />
				<Column dataField="score" caption={t("cupid_col_score")} width={100} />
				<Column dataField="prizeTitleRu" caption={t("cupid_col_prize")} />
				<Column
					dataField="rewardKind"
					caption={t("cupid_col_reward_kind")}
					lookup={{ dataSource: lookupSource(t, REWARD_KINDS), valueExpr: "id", displayExpr: "label" }}
				/>
				<Column dataField="rewardAmount" caption={t("cupid_col_reward_amount")} width={110} />
				<Column
					dataField="status"
					caption={t("cupid_col_status")}
					lookup={{ dataSource: lookupSource(t, WINNER_STATUSES), valueExpr: "id", displayExpr: "label" }}
				/>
				<Column dataField="grantedAtUtc" caption={t("cupid_col_granted_at")} dataType="datetime" />
				<Column dataField="note" caption={t("cupid_col_note")} />
				<Column type="buttons" width={180}>
					<GridButton
						text={t("cupid_btn_mark_granted")}
						visible={(e) => e.row.data.status === "manual_required"}
						onClick={(e) => handleMarkGranted(e.row.data)}
					/>
				</Column>
			</DataGrid>
		</div>
	);
};

const GameCupid = () => {
	const { t } = useTranslation();
	const [seasons, setSeasons] = useState([]);
	const [selectedIndex, setSelectedIndex] = useState(0);

	useEffect(() => {
		(async () => {
			try {
				setSeasons(await getCupidSeasons());
			} catch (e) {
				console.error("Load seasons error:", e);
			}
		})();
	}, []);

	const tabs = [
		{ title: t("cupid_tab_prizes"), key: "prizes" },
		{ title: t("cupid_tab_seasons"), key: "seasons" },
		{ title: t("cupid_tab_leaderboard"), key: "leaderboard" },
		{ title: t("cupid_tab_winners"), key: "winners" },
	];

	return (
		<div className="game-cupid">
			<TabPanel
				dataSource={tabs}
				selectedIndex={selectedIndex}
				onSelectionChanged={(e) => setSelectedIndex(e.component.option("selectedIndex"))}
				animationEnabled={true}
				itemComponent={({ data }) => {
					if (data.key === "prizes") return <PrizesTab t={t} seasons={seasons} />;
					if (data.key === "seasons") return <SeasonsTab t={t} />;
					if (data.key === "winners") return <WinnersTab t={t} seasons={seasons} />;
					return <LeaderboardTab t={t} seasons={seasons} />;
				}}
			/>
		</div>
	);
};

export default GameCupid;
